import { pluralize } from '@core/util/utils';
import { Skeleton } from '@/components/shared/Skeleton';
import CandidateAvatar from '@/modules/timesheets/components/CandidateAvatar';
import { formatTimesheetDateRange } from '@/modules/timesheets/utils/timesheetGroups.utils';

/** The columns this table reads. Approval and pending groups both satisfy it. */
export interface TimesheetTableGroup {
  rowKey: string;
  clientId: string;
  clientName: string;
  userId: string;
  workerName: string;
  workerProfileImageUrl?: string | null;
  items: readonly unknown[];
  startDateISO: string;
  endDateISO: string;
}

interface TimesheetGroupsTableProps {
  groups: TimesheetTableGroup[];
  isLoading: boolean;
  emptyMessage: string;
  /** Client and account manager columns. Hidden for the client portal. */
  showClientColumns: boolean;
  accountManagerByClientId: ReadonlyMap<string, string>;
  /** Shows the selection checkboxes (Requires Approval tab). */
  selectable: boolean;
  selectedRowKeys: Set<string>;
  onToggleGroup: (rowKey: string) => void;
  onToggleAll: (selected: boolean) => void;
  onRowClick: (group: TimesheetTableGroup) => void;
}

const headerClassName =
  'px-4 py-3 text-left text-xs font-semibold tracking-wider text-gray-600';
const cellClassName =
  'border-t border-gray-200 px-4 py-3 align-middle group-hover:bg-gray-50';
/** Shared client rows keep the label, without a line between those candidates. */
const sharedClientCellClassName =
  'border-t-0 px-4 py-3 align-middle group-hover:bg-gray-50';

const TimesheetGroupsTable = ({
  groups,
  isLoading,
  emptyMessage,
  showClientColumns,
  accountManagerByClientId,
  selectable,
  selectedRowKeys,
  onToggleGroup,
  onToggleAll,
  onRowClick,
}: TimesheetGroupsTableProps) => {
  const allSelected =
    groups.length > 0 &&
    groups.every((group) => selectedRowKeys.has(group.rowKey));
  const columnCount =
    (showClientColumns ? 5 : 3) + (selectable ? 1 : 0);

  return (
    <div className="overflow-auto rounded-lg border border-gray-200 bg-white shadow-sm">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {showClientColumns && (
              <>
                <th className={headerClassName}>Client</th>
                <th className={headerClassName}>Account Manager</th>
              </>
            )}
            <th className={headerClassName}>Candidate</th>
            <th className={`${headerClassName} w-36`}># Timesheets</th>
            <th className={headerClassName}>Date</th>
            {selectable && (
              <th className={`${headerClassName} w-12 text-right`}>
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allSelected}
                  disabled={groups.length === 0}
                  onChange={(event) => onToggleAll(event.target.checked)}
                  className="h-4 w-4 cursor-pointer rounded border-gray-300"
                />
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {isLoading && groups.length === 0 ? (
            Array.from({ length: 4 }).map((_, index) => (
              <tr key={index}>
                {showClientColumns && (
                  <>
                    <td className={cellClassName}>
                      <Skeleton height="h-4" width="w-28" />
                    </td>
                    <td className={cellClassName}>
                      <Skeleton height="h-4" width="w-24" />
                    </td>
                  </>
                )}
                <td className={cellClassName}>
                  <Skeleton height="h-4" width="w-40" />
                </td>
                <td className={cellClassName}>
                  <Skeleton height="h-5" width="w-24" />
                </td>
                <td className={cellClassName}>
                  <Skeleton height="h-4" width="w-28" />
                </td>
                {selectable && <td className={cellClassName} />}
              </tr>
            ))
          ) : groups.length === 0 ? (
            <tr>
              <td
                colSpan={columnCount}
                className={`${cellClassName} py-10 text-center text-gray-500`}
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            groups.map((group, index) => {
              const continuesClient =
                showClientColumns &&
                index > 0 &&
                groups[index - 1]?.clientId === group.clientId;
              const clientCellClassName = continuesClient
                ? sharedClientCellClassName
                : cellClassName;
              return (
                <tr
                  key={group.rowKey}
                  onClick={() => onRowClick(group)}
                  className="group cursor-pointer"
                >
                  {showClientColumns && (
                    <>
                      <td
                        className={`${clientCellClassName} font-medium text-gray-900`}
                      >
                        {continuesClient ? null : group.clientName}
                      </td>
                      <td className={`${clientCellClassName} text-gray-700`}>
                        {continuesClient
                          ? null
                          : accountManagerByClientId.get(group.clientId) ||
                            '—'}
                      </td>
                    </>
                  )}
                  <td className={cellClassName}>
                    <div className="flex items-center gap-2">
                      <CandidateAvatar
                        name={group.workerName}
                        imageUrl={group.workerProfileImageUrl}
                      />
                      <span className="font-medium text-gray-900">
                        {group.workerName}
                      </span>
                    </div>
                  </td>
                  <td className={cellClassName}>
                    <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600">
                      {group.items.length}{' '}
                      {pluralize('timesheet', group.items.length)}
                    </span>
                  </td>
                  <td className={`${cellClassName} text-gray-700`}>
                    {formatTimesheetDateRange(
                      group.startDateISO,
                      group.endDateISO,
                    )}
                  </td>
                  {selectable && (
                    <td
                      className={`${cellClassName} text-right`}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Select ${group.workerName} for ${group.clientName}`}
                        checked={selectedRowKeys.has(group.rowKey)}
                        onChange={() => onToggleGroup(group.rowKey)}
                        className="h-4 w-4 cursor-pointer rounded border-gray-300"
                      />
                    </td>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TimesheetGroupsTable;
